import React from "react";
import { motion } from "framer-motion";
import { Layers, BarChart3, FileText, Hash } from "lucide-react";

const outputs = [
  {
    icon: Layers,
    title: "Graft Detection Overlay",
    description:
      "Individual graft locations detected by the model are overlaid on the original scalp image for visual confirmation.",
  },
  {
    icon: BarChart3,
    title: "Density Distribution Map",
    description:
      "A heatmap visualization distinguishing high, medium, and low-density implantation zones across the scalp area.",
  },
  {
    icon: Hash,
    title: "Graft Count Estimate",
    description:
      "The core output: an AI-computed estimate of total grafts detected, presented with a clearly stated accuracy margin.",
  },
  {
    icon: FileText,
    title: "PDF Verification Report",
    description:
      "A structured, shareable report containing the graft count, density breakdown, analysis metadata, and patient details.",
  },
];

const AdvancedPDFReportSection = () => {
  return (
    <section id="analysis" className="relative py-24 sm:py-32 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-14 sm:mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-medium tracking-[0.1em] uppercase mb-6">
            Analysis Output
          </div>
          <h2
            className="text-slate-900 font-semibold tracking-tight leading-tight mb-4"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}
          >
            From image to verified report
          </h2>
          <p className="text-slate-500 text-lg leading-relaxed">
            Every analysis produces structured visual outputs and a downloadable
            report — ready to share, archive, or discuss with your surgeon.
          </p>
        </motion.div>

        {/* Main two-column layout */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-14 lg:items-start">
          {/* Left: actual analysis images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 gap-4 mb-12 lg:mb-0"
          >
            {/* Overlay image */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-slate-400 text-xs font-medium tracking-wider uppercase">
                  Detection Overlay
                </p>
              </div>
              <div className="aspect-square">
                <img
                  src="/assets/images/overlay.png"
                  alt="Graft detection overlay"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Heatmap image */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-slate-400 text-xs font-medium tracking-wider uppercase">
                  Density Heatmap
                </p>
              </div>
              <div className="aspect-square">
                <img
                  src="/assets/images/heatmap.png"
                  alt="Density heatmap"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Report image spanning full width */}
            <div className="col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-slate-400 text-xs font-medium tracking-wider uppercase">
                  PDF Verification Report
                </p>
              </div>
              <img
                src="/assets/images/rapor.jpeg"
                alt="PDF report sample"
                className="w-full object-contain"
              />
            </div>
          </motion.div>

          {/* Right: output descriptions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="space-y-5"
          >
            {outputs.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                className="flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200/80 hover:border-blue-200 hover:shadow-sm transition-all duration-300"
              >
                <div className="w-10 h-10 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <item.icon className="w-5 h-5 text-blue-600 stroke-[1.5]" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-semibold text-sm mb-1">
                    {item.title}
                  </h4>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}

            {/* Note */}
            <p className="text-slate-400 text-xs leading-relaxed pt-2 px-1">
              Analysis results are for informational and documentation purposes.
              The AI model applies deep learning detection with an inherent
              accuracy margin. Calibration adjustments are applied to align model
              output with internal benchmarks.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AdvancedPDFReportSection;
